import { SortDirection } from 'features/drilldown/components/histogram/DrilldownHistogram'
import { DrilldownDataContext } from 'features/drilldown/data/savedExplorationState/DrilldownExplorationContext'
import { toRolodexFilter } from 'features/drilldown/panels/global/filters/DrilldownFilterView'
import {
  DrilldownHistogram,
  useDrilldownHistograms,
  UseHistogramsProps,
} from 'features/drilldown/rolodex/dataHelpers/drilldownDataHelpers'
import { useOntology } from 'features/dynamicOntology'
import range from 'lodash/range'
import reduce from 'lodash/reduce'
import sum from 'lodash/sum'
import uniq from 'lodash/uniq'
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { RolodexHistogramAggregationOp, RolodexHistogramFilter } from 'types'

import {
  DEFAULT_SORT_DIRECTION,
  STACKING_GROUP_OTHER,
  useHistogramConfiguration,
} from './HistogramConfigurationContext'

export interface DrilldownHistogramDataContextType {
  isLoading: boolean
  hasEverLoaded: boolean
  histogram: StackedHistogram
  stackFilters: ReadonlyArray<ReadonlyArray<RolodexHistogramFilter>>
  didTruncationOccur: boolean
  fullRowCount: number
  bucketedCategoryToOriginalCategories: ReadonlyMap<
    string,
    ReadonlyArray<string>
  >
}

export const HistogramDataContext = createContext<
  DrilldownHistogramDataContextType | undefined
>(undefined)

export const HistogramDataContextProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [hasEverLoaded, setHasEverLoaded] = useState<boolean>(false)
  const [hasStartedLoading, setHasStartedLoading] = useState(false)
  const { isLoading } = useContext(DrilldownDataContext)
  const { sortDirection, truncateCount } = useHistogramConfiguration()

  const { stackFilters, isLoading: stackFiltersLoading } = useGetStackFilters()
  const { histograms, isLoading: isHistogramLoading } =
    useGetHistograms(stackFilters)
  const stackedHistogram = useMemo(
    () => stackHistograms(histograms),
    [histograms]
  )
  const { bucketedHistogram, bucketedCategoryToOriginalCategories } =
    useHandleBucketing(stackedHistogram)
  const truncatedSortedBucketedHistogram = useMemo(
    () =>
      typeof truncateCount === 'number'
        ? handleTruncationAndSorting(
            bucketedHistogram,
            sortDirection ?? DEFAULT_SORT_DIRECTION,
            truncateCount
          )
        : bucketedHistogram,
    [bucketedHistogram, truncateCount, sortDirection]
  )

  useEffect(() => {
    setHasStartedLoading(
      (prev) => prev || isLoading || isHistogramLoading || stackFiltersLoading
    )
  }, [isLoading, isHistogramLoading, stackFiltersLoading])

  useEffect(() => {
    setHasEverLoaded(
      hasStartedLoading &&
        !isLoading &&
        !isHistogramLoading &&
        !stackFiltersLoading
    )
  }, [hasStartedLoading, isHistogramLoading, isLoading, stackFiltersLoading])

  const histogramContextData: DrilldownHistogramDataContextType = useMemo(
    () => ({
      isLoading: isLoading || isHistogramLoading || stackFiltersLoading,
      hasEverLoaded,
      bucketedCategoryToOriginalCategories,
      histogram: truncatedSortedBucketedHistogram,
      didTruncationOccur:
        bucketedHistogram.sortedCategories.length >
        truncatedSortedBucketedHistogram.sortedCategories.length,
      fullRowCount: bucketedHistogram.sortedCategories.length,
      stackFilters,
    }),
    [
      isLoading,
      isHistogramLoading,
      stackFiltersLoading,
      hasEverLoaded,
      bucketedCategoryToOriginalCategories,
      truncatedSortedBucketedHistogram,
      bucketedHistogram.sortedCategories.length,
      stackFilters,
    ]
  )

  // Make sure you don't end up selecting bars that aren't there
  const { setSelectedBarIds } = useHistogramConfiguration()
  useEffect(() => {
    if (!hasEverLoaded) {
      return
    }
    setSelectedBarIds((prev) =>
      prev.filter(([barId]) =>
        truncatedSortedBucketedHistogram.sortedCategories.includes(barId)
      )
    )
  }, [hasEverLoaded, setSelectedBarIds, truncatedSortedBucketedHistogram])

  return (
    <HistogramDataContext.Provider value={histogramContextData}>
      {children}
    </HistogramDataContext.Provider>
  )
}

function sortCategories(
  bucketedHistogram: StackedHistogram,
  sortDirection: SortDirection,
  sortByCategory?: boolean
): StackedHistogram {
  const newSortedCategories = bucketedHistogram.sortedCategories.toSorted(
    (a, b) => {
      if (sortByCategory) {
        return a.localeCompare(b)
      }

      const valueA = sum(bucketedHistogram.categoryToValues.get(a))
      const valueB = sum(bucketedHistogram.categoryToValues.get(b))

      if (valueA === valueB) {
        // Ensures deterministic results across asc and desc when sums are equal
        return sortDirection === 'asc' ? b.localeCompare(a) : a.localeCompare(b)
      }

      return sortDirection === 'asc' ? valueB - valueA : valueA - valueB
    }
  )

  return {
    ...bucketedHistogram,
    sortedCategories: newSortedCategories,
  }
}

const handleTruncationAndSorting = (
  histogram: StackedHistogram,
  sortDirection: SortDirection,
  numRows: number
): StackedHistogram => {
  const { sortedCategories, categoryToValues } = sortCategories(
    histogram,
    sortDirection
  )
  const categoriesToKeep = sortedCategories.slice(-numRows)

  return {
    sortedCategories: categoriesToKeep,
    categoryToValues: new Map(
      categoryToValues
        .entries()
        .filter(([key]) => categoriesToKeep.includes(key))
    ),
  }
}

export function useHistogramData(): DrilldownHistogramDataContextType {
  const context = useContext(HistogramDataContext)
  if (!context) {
    throw new Error(
      'Can only use histogram data under the HistogramDataContext'
    )
  }
  return context
}

function useGetIdPools(): {
  idPools: ReadonlyArray<ReadonlyArray<string>>
  isLoading: boolean
} {
  const { plotType, stackingGroups, aggregationColumn } =
    useHistogramConfiguration()
  const { fields } = useOntology()
  const aggColumnDef = fields[aggregationColumn]
  const queries = useMemo(() => {
    const baseQueryProps = {
      groupingType: aggColumnDef.entryType,
      aggregationType: aggColumnDef.entryType,
    }
    const groups =
      plotType === 'stacked'
        ? (stackingGroups?.filter((group) => group.enabled) ?? [])
        : [{ ...STACKING_GROUP_OTHER, name: `${aggColumnDef.entryType}s` }]
    return groups.map(({ filters }) => ({
      ...baseQueryProps,
      filters: filters
        .map(({ state }) => toRolodexFilter(state))
        .filter((rdFilter): rdFilter is RolodexHistogramFilter => !!rdFilter),
    }))
  }, [aggColumnDef.entryType, plotType, stackingGroups])
  const { histograms: idHistograms, isLoading } =
    useDrilldownHistograms(queries)
  const rawIdPools = useMemo(
    () => idHistograms.map((histo) => Object.keys(histo)),
    [idHistograms]
  )
  const idPools = useMemo(() => {
    const usedIds = new Set<string>()
    const filteredPools = rawIdPools.map((pool) => {
      const filteredPool = new Set(pool).difference(usedIds)
      filteredPool.forEach((id) => usedIds.add(id))
      return [...filteredPool]
    })
    return filteredPools
  }, [rawIdPools])
  return useMemo(
    () => ({
      idPools,
      isLoading,
    }),
    [idPools, isLoading]
  )
}

function useGetStackFilters(): {
  stackFilters: ReadonlyArray<ReadonlyArray<RolodexHistogramFilter>>
  isLoading: boolean
} {
  const { aggregationColumn } = useHistogramConfiguration()
  const { idPools, isLoading } = useGetIdPools()
  const { fields } = useOntology()
  const { entryType: aggType } = fields[aggregationColumn]
  const filters = useMemo<ReadonlyArray<ReadonlyArray<RolodexHistogramFilter>>>(
    () =>
      idPools.map((idPool) => [
        {
          type: aggType,
          field: 'id',
          op: 'in',
          value: [...idPool],
        },
      ]),
    [aggType, idPools]
  )
  return useMemo(
    () => ({ stackFilters: filters, isLoading }),
    [filters, isLoading]
  )
}

/**
 * Generate the histograms for the Histogram panel configuration.
 */
function useGetHistograms(
  stackFilters: ReadonlyArray<ReadonlyArray<RolodexHistogramFilter>>
) {
  const { groupingColumn, aggregationColumn, aggregationOp } =
    useHistogramConfiguration()
  const { fields } = useOntology()
  const {
    entryType: groupingType,
    key: groupingKey,
    keyOrField: histogramGroupByPropertyKey,
  } = fields[groupingColumn]
  const {
    entryType: aggregationType,
    key: aggregationKey,
    keyOrField: aggregationPropertyKey,
  } = fields[aggregationColumn]
  const baseHistogramHookProps = useMemo<Omit<UseHistogramsProps, 'filters'>>(
    () => ({
      groupingType,
      aggregationType,
      groupingColumn: groupingKey,
      aggregationColumn: aggregationKey,
      histogramGroupByPropertyKey,
      aggregationPropertyKey,
      op: aggregationOp,
      aggCast:
        aggregationOp === undefined || aggregationOp === 'count'
          ? undefined
          : 'number',
    }),
    [
      aggregationKey,
      aggregationOp,
      aggregationPropertyKey,
      aggregationType,
      groupingKey,
      groupingType,
      histogramGroupByPropertyKey,
    ]
  )
  // generate the queries
  const histogramHookProps = useMemo<ReadonlyArray<UseHistogramsProps>>(
    () =>
      stackFilters.map((filters) => ({
        ...baseHistogramHookProps,
        filters,
      })),
    [baseHistogramHookProps, stackFilters]
  )
  // execute them
  const { isLoading, histograms } = useDrilldownHistograms(histogramHookProps)
  // These histograms are totals, so each one has a count of it's own stack filters,
  // and all suesequent stacks.
  // We just need to substract subsequent counts from each one to make them independent
  // counts
  return useMemo(() => ({ isLoading, histograms }), [histograms, isLoading])
}

// Given the multiple hisograms from the group queries, join them back together.
function stackHistograms(
  histograms: ReadonlyArray<DrilldownHistogram>,
  sortByCategory?: boolean
): StackedHistogram {
  const categoryToValues = reduce(
    histograms,
    (acc, histogram, idx) => {
      const newMap = new Map(acc)
      Object.entries(histogram).forEach(([groupId, count]) => {
        const currGroupValues = acc.get(groupId) ?? range(0, idx).map(() => 0)
        const newGroupValues = [...currGroupValues, count]
        newMap.set(groupId, newGroupValues)
      })
      return newMap
    },
    new Map<string, ReadonlyArray<number>>()
  )
  return {
    sortedCategories: [...categoryToValues.keys()].toSorted((a, b) =>
      sortByCategory
        ? a.localeCompare(b)
        : sum(categoryToValues.get(a)) - sum(categoryToValues.get(b))
    ),
    categoryToValues,
  }
}

const BUCKETING_COMBINATORS: Record<
  RolodexHistogramAggregationOp,
  (val1: number, val2: number) => number
> = {
  count: (val1, val2) => val1 + val2,
  sum: (val1, val2) => val1 + val2,
  min: Math.min,
  max: Math.max,
}

// Handle bucketing if it was configured - for example with the
// inspection timeline.
function useHandleBucketing(histogram: StackedHistogram): {
  bucketedHistogram: StackedHistogram
  bucketedCategoryToOriginalCategories: ReadonlyMap<
    string,
    ReadonlyArray<string>
  >
} {
  const { groupingColumn, dateBucketingConfiguration, aggregationOp } =
    useHistogramConfiguration()

  const { fields } = useOntology()
  const { type: fieldType } = fields[groupingColumn]
  const combine = BUCKETING_COMBINATORS[aggregationOp ?? 'count']
  return useMemo(() => {
    if (fieldType === 'date' && dateBucketingConfiguration?.bucketingFunction) {
      const bucketedCategoryToCategories: ReadonlyMap<
        string,
        ReadonlyArray<string>
      > = reduce(
        histogram.sortedCategories,
        (acc, category) => {
          const bucket = dateBucketingConfiguration.bucketingFunction(category)
          const sourceCategories = acc.get(bucket) ?? []
          const newMap = new Map(acc)
          newMap.set(bucket, uniq([...sourceCategories, category]))
          return newMap
        },
        new Map()
      )

      const bucketedCategoryToValues = new Map(
        bucketedCategoryToCategories.entries().map(([bucket, categories]) => {
          const stacks = categories
            .map((category) => histogram.categoryToValues.get(category))
            .filter((stack): stack is ReadonlyArray<number> => !!stack)
          const stackHeight = stacks[0]?.length ?? -1
          const combinedStack = reduce(
            stacks,
            (acc, stack) => acc.map((val, idx) => combine(val, stack[idx])),
            range(0, stackHeight).map(() => 0)
          )
          return [bucket, combinedStack]
        })
      )

      return {
        bucketedHistogram: {
          sortedCategories: [...bucketedCategoryToCategories.keys()],
          categoryToValues: bucketedCategoryToValues,
        },
        bucketedCategoryToOriginalCategories: bucketedCategoryToCategories,
      }
    }

    return {
      bucketedHistogram: histogram,
      bucketedCategoryToOriginalCategories: new Map(
        histogram.categoryToValues.keys().map((k) => [k, [k]])
      ),
    }
  }, [combine, dateBucketingConfiguration, fieldType, histogram])
}

export type StackedHistogram = {
  sortedCategories: ReadonlyArray<string>
  categoryToValues: ReadonlyMap<string, ReadonlyArray<number>>
}
